import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Input, Button, Switch, Select, Space, Divider, Alert, Tag, message } from 'antd';
import { FacebookOutlined, InfoCircleOutlined, ApiOutlined, CheckCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { metaAdsApi } from '../../utils/api';
import ChampRessource from '../metaAds/ChampRessource';

/**
 * Configuration de la connexion au compte Meta Ads (Marketing API).
 *
 * Étapes pour l'utilisateur :
 *  1. Coller un token d'accès système Meta (permissions ads_management, business_management)
 *  2. Cliquer sur "Tester la connexion" → les comptes publicitaires, Pages et catalogues
 *     accessibles sont récupérés depuis Meta et proposés dans des listes déroulantes
 *  3. Sélectionner les ressources à utiliser puis enregistrer
 *
 * Le token n'est jamais renvoyé en clair par le serveur : le champ reste vide après
 * enregistrement et un aperçu masqué est affiché. Laisser le champ vide conserve le token existant.
 */

// Pays proposés par défaut pour le ciblage (la saisie libre reste possible via le mode "tags")
const PAYS_SUGGERES = [
  { code: 'CI', nom: "Côte d'Ivoire" },
  { code: 'SN', nom: 'Sénégal' },
  { code: 'ML', nom: 'Mali' },
  { code: 'BF', nom: 'Burkina Faso' },
  { code: 'GN', nom: 'Guinée' },
  { code: 'BJ', nom: 'Bénin' },
  { code: 'TG', nom: 'Togo' },
  { code: 'CM', nom: 'Cameroun' },
  { code: 'FR', nom: 'France' }
];

const MetaAdsSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [oauthConnecting, setOauthConnecting] = useState(false);
  const [tokenEnregistre, setTokenEnregistre] = useState(null); // aperçu masqué du token existant
  const [sourceConnexion, setSourceConnexion] = useState(null);
  const [assets, setAssets] = useState(null); // ressources Meta récupérées après vérification

  // Charger la configuration existante
  const chargerConfiguration = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await metaAdsApi.getSettings();

      if (data?.data) {
        const config = data.data;
        form.setFieldsValue({
          meta_ads_account_id: config.meta_ads_account_id,
          meta_ads_page_id: config.meta_ads_page_id,
          meta_ads_instagram_id: config.meta_ads_instagram_id,
          meta_ads_whatsapp_number: config.meta_ads_whatsapp_number,
          meta_ads_catalog_id: config.meta_ads_catalog_id,
          meta_ads_pixel_id: config.meta_ads_pixel_id,
          meta_ads_default_countries: config.meta_ads_default_countries || ['CI'],
          meta_ads_enabled: config.meta_ads_enabled || false
        });
        setTokenEnregistre(config.has_access_token ? config.access_token_preview : null);
        setSourceConnexion(config.meta_ads_connection_source || null);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la configuration Meta Ads:', error);
      message.error('Erreur lors du chargement de la configuration Meta Ads');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    chargerConfiguration();
  }, [chargerConfiguration]);

  /**
   * Affiche le résultat du retour OAuth puis enlève les paramètres temporaires de
   * l'URL, afin qu'un simple rafraîchissement ne rejoue pas le message de connexion.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resultat = params.get('metaAdsConnection');
    if (!resultat) return;

    if (resultat === 'success') {
      message.success('Compte Meta connecté. Choisissez maintenant les ressources à utiliser.');
      chargerConfiguration();
    } else {
      message.error('La connexion Meta n’a pas pu être finalisée. Vous pouvez la relancer.');
    }

    params.delete('metaAdsConnection');
    params.delete('tab');
    const query = params.toString();
    window.history.replaceState({}, document.title, `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
  }, [chargerConfiguration]);

  /**
   * Démarre la connexion sécurisée vers Meta. L'API fournit une URL OAuth signée,
   * puis le navigateur quitte temporairement Wabot pour l'écran officiel de Meta.
   */
  const connecterAvecMeta = async () => {
    try {
      setOauthConnecting(true);
      const { data } = await metaAdsApi.getOAuthAuthorizationUrl();
      window.location.assign(data.data.authorizationUrl);
    } catch (error) {
      console.error('Erreur lors du démarrage de la connexion OAuth Meta:', error);
      message.error(error.response?.data?.message || 'La connexion Meta est indisponible pour le moment.');
      setOauthConnecting(false);
    }
  };

  /**
   * Teste la connexion à Meta avec le token saisi (ou celui déjà enregistré si le champ est vide)
   * et récupère les comptes publicitaires, Pages et catalogues accessibles.
   */
  const testerConnexion = async () => {
    try {
      setVerifying(true);
      const accessToken = form.getFieldValue('meta_ads_access_token');
      const adAccountId = form.getFieldValue('meta_ads_account_id');

      const { data } = await metaAdsApi.verify({ accessToken, adAccountId });

      setAssets(data.data);
      message.success(data.message || 'Connexion Meta réussie');

      // Pré-sélectionner automatiquement le compte s'il n'y en a qu'un seul
      const comptes = data.data.adAccounts || [];
      if (comptes.length === 1 && !adAccountId) {
        form.setFieldsValue({ meta_ads_account_id: comptes[0].id });
      }
    } catch (error) {
      console.error('Erreur lors du test de connexion Meta:', error);
      message.error(error.response?.data?.message || 'Impossible de se connecter à Meta');
      setAssets(null);
    } finally {
      setVerifying(false);
    }
  };

  /**
   * Enregistre la configuration.
   * Si le champ token est vide, le token déjà enregistré côté serveur est conservé.
   */
  const enregistrerConfiguration = async (values) => {
    try {
      setSaving(true);

      const payload = { ...values };
      // Ne pas transmettre un token vide : le serveur conserverait alors l'existant
      if (!payload.meta_ads_access_token) delete payload.meta_ads_access_token;

      const { data } = await metaAdsApi.saveSettings(payload);

      setTokenEnregistre(data.data?.has_access_token ? data.data.access_token_preview : null);
      form.setFieldsValue({ meta_ads_access_token: '' });
      message.success('Configuration Meta Ads enregistrée');
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de la configuration Meta Ads:', error);
      message.error(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Options des listes déroulantes construites à partir des ressources Meta récupérées
  const optionsComptes = (assets?.adAccounts || []).map(compte => ({
    value: compte.id,
    label: `${compte.name} (${compte.id}${compte.currency ? ` · ${compte.currency}` : ''})`
  }));

  const optionsPages = (assets?.pages || []).map(page => ({
    value: page.id,
    label: `${page.name} (${page.id})`
  }));

  const optionsCatalogues = (assets?.catalogs || []).map(catalogue => ({
    value: catalogue.id,
    label: `${catalogue.name} (${catalogue.product_count ?? 0} produits)`
  }));

  // Pixels du compte publicitaire (récupérés avec le token, sans permission supplémentaire)
  const optionsPixels = (assets?.pixels || []).map(pixel => ({
    value: pixel.id,
    label: `${pixel.name} (${pixel.id})`
  }));

  // Comptes Instagram liés aux Pages récupérées
  const optionsInstagram = (assets?.pages || [])
    .filter(page => page.instagram_business_account)
    .map(page => ({
      value: page.instagram_business_account.id,
      label: `@${page.instagram_business_account.username || page.instagram_business_account.id} (via ${page.name})`
    }));

  return (
    <div className="space-y-6">
      <Card
        title={
          <div className="flex items-center space-x-2">
            <FacebookOutlined className="text-blue-600" data-ui-preserve-color={true /* Conserve le repère métier, graphique ou de marque. */} />
            <span>Connexion au compte Meta Ads</span>
          </div>
        }
        className="shadow-sm"
        loading={loading}
      >
        <Alert
          type={tokenEnregistre ? 'success' : 'info'}
          showIcon
          className="mb-4"
          message={tokenEnregistre ? 'Compte Meta Ads connecté' : 'Connectez votre compte Meta Ads'}
          description={
            tokenEnregistre
              ? `Votre compte est relié${sourceConnexion === 'oauth' ? ' via la connexion sécurisée Meta' : ''}. Vous pouvez choisir les ressources ci-dessous.`
              : 'Connectez-vous chez Meta : vos comptes publicitaires et ressources seront récupérés automatiquement.'
          }
          action={(
            <Button type="primary" icon={<LinkOutlined />} loading={oauthConnecting} onClick={connecterAvecMeta}>
              {tokenEnregistre ? 'Reconnecter Meta' : 'Connecter mon compte Meta'}
            </Button>
          )}
        />

        <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-2">
            <InfoCircleOutlined className="text-blue-600 mt-1" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Connexion simplifiée recommandée</p>
              <p className="mb-1">
                Cliquez sur <strong>Connecter mon compte Meta</strong> : Meta vous demande directement
                l'autorisation et Mercuri Shop récupère vos comptes publicitaires sans mot de passe ni token à copier.
              </p>
              <p className="font-medium mb-1 mt-3">Connexion manuelle (réservée aux administrateurs)</p>
              <p className="mb-1">
                Dans <strong>business.facebook.com</strong> → Paramètres d'entreprise → Utilisateurs système :
                créez un utilisateur système, attribuez-lui votre compte publicitaire <strong>et votre Page</strong>
                (Ajouter des actifs, accès total), puis générez un token avec les permissions
                <code> ads_management</code>, <code>ads_read</code> et <code>business_management</code>.
              </p>
              <p>
                Collez le token ci-dessous puis cliquez sur <strong>Tester la connexion</strong> :
                vos comptes publicitaires sont chargés automatiquement. Les Pages et catalogues ne
                s'affichent que si le token possède aussi <code>pages_show_list</code> et
                <code> catalog_management</code> — sinon, saisissez leurs identifiants à la main.
              </p>
            </div>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={enregistrerConfiguration}
          className="max-w-2xl"
          initialValues={{ meta_ads_default_countries: ['CI'], meta_ads_enabled: false }}
          onValuesChange={(champsModifies) => {
            // Les pixels dépendent du compte publicitaire : on relance la découverte
            // dès qu'il change, sinon la liste resterait vide après le premier test
            if (champsModifies.meta_ads_account_id && assets) testerConnexion();
          }}
        >
          <Form.Item
            name="meta_ads_enabled"
            label="Activer la création de publicités Meta"
            valuePropName="checked"
            extra="Désactivez pour suspendre toute création de campagne depuis le site"
          >
            <Switch />
          </Form.Item>

          <Divider />

          <Form.Item
            name="meta_ads_access_token"
            label="Token d'accès Meta"
            extra={
              tokenEnregistre
                ? `Un token est déjà enregistré (${tokenEnregistre}). Laissez vide pour le conserver.`
                : 'Token système avec les permissions ads_management et business_management'
            }
          >
            <Input.Password placeholder={tokenEnregistre ? 'Token enregistré — laisser vide pour conserver' : 'Collez votre token Meta'} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                onClick={testerConnexion}
                loading={verifying}
                icon={<ApiOutlined />}
              >
                Tester la connexion
              </Button>
              {assets && (
                <Tag icon={<CheckCircleOutlined />} color="success">
                  {assets.adAccounts?.length || 0} compte(s) · {assets.pages?.length || 0} page(s) ·{' '}
                  {assets.pixels?.length || 0} pixel(s) · {assets.catalogs?.length || 0} catalogue(s)
                </Tag>
              )}
            </Space>
          </Form.Item>

          {assets?.account && (
            <Alert
              type="info"
              showIcon
              className="mb-4"
              message={`Compte publicitaire : ${assets.account.name}`}
              description={`Devise ${assets.account.currency} · Statut ${assets.account.account_status === 1 ? 'actif' : 'inactif ou restreint'}`}
            />
          )}

          <Divider>Ressources publicitaires</Divider>

          <Form.Item
            name="meta_ads_account_id"
            label="Compte publicitaire"
            rules={[{ required: true, message: 'Sélectionnez ou saisissez un compte publicitaire' }]}
            extra="Format act_XXXXXXXXX — chargé automatiquement après le test de connexion"
          >
            <ChampRessource options={optionsComptes} placeholder="act_123456789" />
          </Form.Item>

          <Form.Item
            name="meta_ads_page_id"
            label="Page Facebook"
            rules={[{ required: true, message: 'Sélectionnez ou saisissez une Page Facebook' }]}
            extra="Page qui apparaîtra comme annonceur sur les publicités"
          >
            <ChampRessource options={optionsPages} placeholder="ID de votre Page Facebook" />
          </Form.Item>

          <Form.Item
            name="meta_ads_instagram_id"
            label="Compte Instagram (optionnel)"
            extra="Nécessaire uniquement pour diffuser également sur Instagram"
          >
            <ChampRessource options={optionsInstagram} placeholder="ID du compte Instagram professionnel" />
          </Form.Item>

          <Form.Item
            name="meta_ads_whatsapp_number"
            label="Numéro WhatsApp Business (optionnel)"
            extra="Utilisé pour les publicités Click-to-WhatsApp. Format international, ex : +221771234567"
          >
            <Input placeholder="+221771234567" />
          </Form.Item>

          <Form.Item
            name="meta_ads_catalog_id"
            label="Catalogue produits (optionnel)"
            extra="Utilisé pour les campagnes Advantage+ Catalogue"
          >
            <ChampRessource options={optionsCatalogues} placeholder="ID du catalogue Meta" />
          </Form.Item>

          <Form.Item
            name="meta_ads_pixel_id"
            label="Pixel pour l'optimisation"
            extra="Nécessaire pour les objectifs Conversions et Advantage+ Catalogue. Chargé automatiquement après le test de connexion."
          >
            <ChampRessource options={optionsPixels} placeholder="Ex: 1234567890123456" />
          </Form.Item>

          <Form.Item
            name="meta_ads_default_countries"
            label="Pays ciblés par défaut"
            extra="Codes ISO à 2 lettres. Vous pouvez saisir d'autres codes que ceux proposés."
          >
            <Select
              mode="tags"
              placeholder="Sélectionnez un ou plusieurs pays"
              options={PAYS_SUGGERES.map(p => ({ value: p.code, label: `${p.nom} (${p.code})` }))}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving} icon={<FacebookOutlined />}>
                Enregistrer la configuration
              </Button>
              <Button onClick={chargerConfiguration} loading={loading}>
                Recharger
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default MetaAdsSettings;
